// TelcoTrack — persisted manual contract fill on bremena_instruments.fill_data.

import {
  isBremenaConsentKind,
  type BremenaConsentKind,
} from '../constants/bremena.constants';

/**
 * Manual Mustache overrides stored on the instrument (JSON `fill_data`).
 * Owner/parcel identity stays on subjects/parcels; these are contract-only gaps
 * that used to require Google Docs editing.
 */
export interface BremenaInstrumentFillData {
  /**
   * Consent subtype when `instrument_type = souhlas`
   * (`WRITTEN_CONSENT_104` | `OWNER_CONSENT_187`).
   */
  consentKind?: BremenaConsentKind | string | null;
  /** Mustache `Cislo_smlouvy` — never derived from SAP. */
  cisloSmlouvy?: string | null;
  /** Šířka VB (m) — Mustache `Sirka_VB`. */
  sirkaVb?: string | number | null;
  /** Popis zařízení na pozemku — Mustache `Popis_zarizeni`. */
  popisZarizeni?: string | null;
  /** Výše náhrady (Kč). */
  castkaNahrada?: string | number | null;
  /** Explicitní bezeplatná smlouva (0 Kč OK). */
  bezuplatne?: boolean | null;
  /** Override SAP (also mirrored to `sap_code` when saved via API). */
  sapKod?: string | null;
  /** Override KIA (also mirrored to `kia_code` when saved via API). */
  kiaKod?: string | null;
  /** Mustache `Oznaceni_stavby` when polygon name is insufficient. */
  oznaceniStavby?: string | null;
  obec?: string | null;
  okres?: string | null;
  /** Datum CETIN — ISO or free text; empty = leave blank in template. */
  datum?: string | null;

  // ——— Varianta vzoru (katalog `@cubby/shared` bremena/templates) ———
  /** Ruční přepis varianty vzoru (`BremenaTemplateVariantId`); prázdné = automatické pravidlo. */
  templateVariant?: string | null;
  /** Ruční předmět smlouvy (`pozemek` | `budova` | `pozemek_budova` | `jednotka`). */
  objectKind?: string | null;
  /** Předmět nezapsaný v KN (3.4 / 4.4). */
  nezapsanoVKn?: boolean | null;
  /** Zmocněnec CETIN v hlavičce (`protelco` | `cetin` | `nova_optika`). */
  zastupce?: string | null;
  /** Vypustit sankční doložku (municipality). */
  bezSankci?: boolean | null;

  // ——— Budova / jednotky ———
  jednotky?: string | null;
  popisZarizeniBudova?: string | null;

  // ——— Náhrada / platba ———
  /** `jednorazova` | `rozdelena` | `za_bm` | `znalecky_posudek` */
  nahradaTyp?: string | null;
  castkaZaBm?: string | number | null;
  /** Délka vedení (m) pro výpočet náhrady při `za_bm` (jinak z VB na parcelách). */
  delkaVb?: string | number | null;
  castkaNahradaPozemek?: string | number | null;
  castkaNahradaBudova?: string | number | null;
  znalec?: string | null;
  zpCislo?: string | null;
  zpDatum?: string | null;
  /** `po_vkladu` | `po_podpisu` | `zaloha` (jen plátce DPH). */
  platbaTyp?: string | null;
  variabilniSymbol?: string | null;

  // ——— Kontakt / schválení / GP / SUZ ———
  kontaktEmail?: string | null;
  kontaktTelefon?: string | null;
  schvaleniOrgan?: string | null;
  usneseniCislo?: string | null;
  usneseniDatum?: string | null;
  gpCislo?: string | null;
  gpDatum?: string | null;
  /** `doba_urcita` | `zivotnost` */
  suzTrvani?: string | null;
  dobaTrvaniLet?: string | number | null;

  /**
   * Keys the user edited on the contract draft. „Doplnit z podkladů“ never overwrites them,
   * even when the value was cleared.
   */
  manualKeys?: string[] | null;

  /** Adresa portálu stavebníka zadaná uživatelem (asistovaný krok, ne CSV upload). */
  portalUrl?: string | null;
  /** Uživatel označil odeslání na portál stavebníka. */
  portalOdeslano?: boolean | null;
  /** Volitelné číslo řízení po označení vkladu jako podaného. */
  katastrCisloRizeni?: string | null;
}

const STRING_KEYS = [
  'cisloSmlouvy',
  'sirkaVb',
  'popisZarizeni',
  'castkaNahrada',
  'sapKod',
  'kiaKod',
  'oznaceniStavby',
  'obec',
  'okres',
  'datum',
  'templateVariant',
  'objectKind',
  'zastupce',
  'jednotky',
  'popisZarizeniBudova',
  'nahradaTyp',
  'castkaZaBm',
  'delkaVb',
  'castkaNahradaPozemek',
  'castkaNahradaBudova',
  'znalec',
  'zpCislo',
  'zpDatum',
  'platbaTyp',
  'variabilniSymbol',
  'kontaktEmail',
  'kontaktTelefon',
  'schvaleniOrgan',
  'usneseniCislo',
  'usneseniDatum',
  'gpCislo',
  'gpDatum',
  'suzTrvani',
  'dobaTrvaniLet',
  'portalUrl',
  'katastrCisloRizeni',
] as const;

const BOOLEAN_KEYS = ['bezuplatne', 'nezapsanoVKn', 'bezSankci', 'portalOdeslano'] as const;

function readManualKeys(raw: Record<string, unknown>): string[] {
  const value = raw['manualKeys'];
  if (!Array.isArray(value)) return [];
  const keys = value.map((item) => String(item).trim()).filter(Boolean);
  return [...new Set(keys)];
}

/** Klíče `fill_data`, které patří variantám vzorů (pro UI Doplnit / dokumentaci). */
export const BREMENA_FILL_TEMPLATE_KEYS = STRING_KEYS.slice(10);

function trimNullable(value: unknown): string | null {
  if (value == null) return null;
  const s = String(value).trim();
  return s || null;
}

/** Drop empty strings / nulls; keep boolean flags (`bezuplatne`, `nezapsanoVKn`, `bezSankci`) only when boolean. */
export function normalizeBremenaInstrumentFillData(
  raw: BremenaInstrumentFillData | Record<string, unknown> | null | undefined,
): BremenaInstrumentFillData | null {
  if (raw == null || typeof raw !== 'object') return null;
  const out: BremenaInstrumentFillData = {};
  const consentKindRaw = trimNullable((raw as Record<string, unknown>)['consentKind']);
  if (consentKindRaw && isBremenaConsentKind(consentKindRaw)) {
    out.consentKind = consentKindRaw;
  }
  for (const key of STRING_KEYS) {
    const v = trimNullable((raw as Record<string, unknown>)[key]);
    if (v != null) (out as Record<string, unknown>)[key] = v;
  }
  for (const key of BOOLEAN_KEYS) {
    const v = (raw as Record<string, unknown>)[key];
    if (typeof v === 'boolean') (out as Record<string, unknown>)[key] = v;
  }
  const manualKeys = readManualKeys(raw as Record<string, unknown>);
  if (manualKeys.length) out.manualKeys = manualKeys;
  return Object.keys(out).length ? out : null;
}

export function mergeBremenaFillData(
  existing: BremenaInstrumentFillData | null | undefined,
  patch: BremenaInstrumentFillData | Record<string, unknown> | null | undefined,
): BremenaInstrumentFillData | null {
  if (patch === null) return null;
  if (patch === undefined) return normalizeBremenaInstrumentFillData(existing);
  const base = { ...(existing ?? {}) } as Record<string, unknown>;
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) continue;
    if (value === null || (typeof value === 'string' && !value.trim())) {
      delete base[key];
      continue;
    }
    base[key] = value;
  }
  return normalizeBremenaInstrumentFillData(base);
}
